// @flow
import React, {type Node} from 'react'
import {type RelayFragmentContainer, createFragmentContainer, graphql} from 'react-relay'

import AccordianField from 'components/proposals/Part/ui/AccordianField'
import type {DeleteCallback, PickAlert, UpdateCallback} from 'components/proposals/types'
import FrogTipBox, {MarkdownTip} from 'components/ui/FrogTipBox'
import Value from 'components/proposals/Part/ui/Value'
import {useProposalContext} from 'contexts/ProposalContext'

import isValidAccountability from './isValidAccountability'

import type {Accountabilities_organization as Organization}
  from './__generated__/Accountabilities_organization.graphql'
import type {Accountabilities_circle as Circle}
  from './__generated__/Accountabilities_circle.graphql'

type Props = $ReadOnly<{
  accountabilities: Value[],
  circle: ?Circle,
  isReadonly: boolean,
  isRemovingRoleOrCircle: boolean,
  onChange: UpdateCallback,
  onDelete: DeleteCallback,
  organization: Organization,
  pickAlert: PickAlert,
  enterOverride: 'NEWLINE' | 'NEW_ITEM',
  changeEnterOverride: ('NEWLINE' | 'NEW_ITEM') => void,
}>

export function accountabilityValidator(organization: Organization, isRemoving: boolean):
((str: string) => { className?: string, message?: string, ... }) {
  return (str: string) => {
    if (isRemoving)
      return {}

    const valid = isValidAccountability(
      str,
      organization.locale,
      organization.accountatibilityCheckingIsEnabled,
    )

    return valid
      ? {}
      : {
        className: 'has-warning',
        message: I18n.t('proposals.react.accountabilities_should_begin'),
      }
  }
}

const markdownTip = () => (
  <MarkdownTip
    text={I18n.t('org_nav.tabs.overview_tab.accountabilities.markdown_tip')}
    bottomLink={I18n.t('org_nav.tabs.overview_tab.accountabilities.learn_more_link')}
  />
)

function Accountabilities({isRemovingRoleOrCircle,
  organization,
  circle,
  accountabilities,
  onChange,
  onDelete,
  pickAlert,
  enterOverride,
  changeEnterOverride,
  isReadonly}: Props): Node {
  const validateAccountability = accountabilityValidator(organization, isRemovingRoleOrCircle)
  const proposalContext = useProposalContext()

  return (
    <AccordianField
      addItemTitle={I18n.t('shared.add_new')}
      circle={circle}
      className="test-accountabilities-container"
      isReadonly={isReadonly}
      label={(
        <FrogTipBox
          withBottomSpacing
          hideDivider
          tip={markdownTip}
          target={organization.accountabilitiesLabel}
          helpFor={organization.accountabilitiesLabel}
        />
      )}
      enterOverride={enterOverride}
      changeEnterOverride={changeEnterOverride}
      organization={organization}
      path={['accountabilities']}
      pickAlert={pickAlert}
      onDelete={onDelete}
      onChange={onChange}
      strikethrough={isRemovingRoleOrCircle}
      validate={validateAccountability}
      values={accountabilities}
      aiSuggestProps={circle ? {
        context: {type: 'proposal', id: proposalContext?.databaseId},
        promptKey: 'suggest_proposal_accountability',
        data: {
          tensionBody: proposalContext?.tension?.body || '',
          circle: {ref_type: 'Circle', ref_id: circle.databaseId},
        },
      } : undefined}
      placeholder={I18n.t('role.placeholders.accountability')}
    />
  )
}

export default (createFragmentContainer(Accountabilities, {
  organization: graphql`
    fragment Accountabilities_organization on Organization {
      accountabilitiesLabel: termFor(name: "accountabilities")
      accountatibilityCheckingIsEnabled
      databaseId
      locale

      ...WYSIWYGTextareaWithFeatureFlag_organization
    }
  `,
  circle: graphql`
    fragment Accountabilities_circle on Circle {
      databaseId
    }
  `,
}): RelayFragmentContainer<typeof(Accountabilities)>)
